import { TextField, useTheme } from '@material-ui/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { checkAuth, clearMfaContext } from 'actions/app.actions';
import { AuthAPI } from 'services/auth.api';
import { LinkWithUIGuidelines } from 'component-library/atoms/LinkWithUIGuidelines';
import { PrimaryButton } from 'components';
import SubmittingOverlay from 'components/SubmittingOverlay';
import useQuery from 'core/utils/use-query.hook';
import { ICustomTheme } from 'interfaces';
import { appConfigSelector, mfaTempTokenSelector } from 'reducers/app.reducer';
import redirectAfterAuth from 'utils/redirect-after-auth';
import { ROUTES } from '../../constants';
import CountdownTimer from './CountdownTimer';
import * as S from './styles';
import {
  MfaErrorStatus,
  useMfaCodeResend,
  useMfaCodeVerify,
} from './useMfaCode';

const RESEND_DELAY_SECONDS = 60;

type FormValues = {
  code: string;
};

export default function MfaCodeVerify(): JSX.Element | null {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();
  const { programEmail } = useSelector(appConfigSelector);
  const { returnPath } = useQuery();
  const [isSuccess, setIsSuccess] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const emailLinkComponents = {
    emailLink: (
      <LinkWithUIGuidelines
        url={`mailto:${programEmail}`}
        textLink=""
        isInternalLink={false}
        color={theme.colors.paletteFromBO.main}
        withUnderline
      />
    ),
  };

  const [blockedAccount, setBlockedAccount] = useState(false);
  // Timestamp-based resend cooldown — only changes on mount and after each resend
  const [resendEndTime, setResendEndTime] = useState(
    () => Date.now() + RESEND_DELAY_SECONDS * 1000,
  );
  // Ref to check cooldown in callbacks without causing re-renders
  const resendEndTimeRef = useRef(resendEndTime);
  resendEndTimeRef.current = resendEndTime;

  const [resendLimitReached, setResendLimitReached] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const tempToken = useSelector(mfaTempTokenSelector);

  const { register, handleSubmit, errors, setError, setValue } =
    useForm<FormValues>({
      defaultValues: { code: '' },
    });

  useEffect(() => {
    if (!tempToken && !isSuccess) {
      history.replace(ROUTES.LOGIN);
    }
  }, [tempToken, history, isSuccess]);

  const { mutate: verifyCode, isLoading: isVerifying } = useMfaCodeVerify();

  const { mutate: resendCodeByEmail, isLoading: isResendingByEmail } =
    useMfaCodeResend();

  const onSubmit = useCallback(
    ({ code }: FormValues) => {
      if (!tempToken) return;

      verifyCode(
        { token: tempToken, code },
        {
          onSuccess: async () => {
            setIsSuccess(true);
            dispatch(clearMfaContext());
            // Attendre que GET /me confirme que le cookie max_api_token est opérationnel.
            // checkAuth.success() met isAuthenticated: true dans le reducer — on ne redirige
            // qu'après, sinon Content.tsx déclenche ses appels API avant que le cookie soit disponible.
            try {
              const { data: userInfos } = await AuthAPI.checkAuth();
              dispatch(checkAuth.success(userInfos));
            } catch {
              // GET /me échoué : le cookie n'est pas valide, retour au login
              setIsSuccess(false);
              history.replace(ROUTES.LOGIN);
              return;
            }
            // Redirection intelligente avec returnPath
            redirectAfterAuth(returnPath);
          },
          onError: (error) => {
            const data = error.response?.data;
            const status = data?.status as MfaErrorStatus | undefined;

            if (status === MfaErrorStatus.MISSING_CODE_OR_TOKEN) {
              setSessionExpired(true);
              setError('code', {
                type: 'manual',
                message: t('auth.mfa.session_expired') as string,
              });

              return;
            }

            const message = status
              ? (t(`auth.mfa.errors.${status}`, {
                  remainingSeconds: data?.remainingSeconds,
                }) as string)
              : (t('auth.mfa.errors.UNKNOWN') as string);

            setError('code', { type: 'manual', message });
            if (status === MfaErrorStatus.ACCOUNT_TEMPORARILY_BLOCKED) {
              setBlockedAccount(true);
            }
          },
          onSettled: () => {
            setValue('code', '');
          },
        },
      );
    },
    [
      tempToken,
      verifyCode,
      dispatch,
      history,
      setError,
      setValue,
      t,
      returnPath,
    ],
  );

  const handleResendCodeByEmail = useCallback(() => {
    if (!tempToken) return;
    // Check cooldown via ref (no re-render dependency)
    if (resendEndTimeRef.current > Date.now()) return;

    setResendSuccess(false);
    setResendError(null);

    resendCodeByEmail(
      { token: tempToken },
      {
        onSuccess: (response) => {
          setResendSuccess(true);
          const newEndTime = Date.now() + RESEND_DELAY_SECONDS * 1000;
          setResendEndTime(newEndTime);

          if (response.data?.status === MfaErrorStatus.LAST_CODE_RESENT) {
            setResendLimitReached(true);
          }
        },
        onError: (error) => {
          const data = error.response?.data;
          const status = data?.status as MfaErrorStatus | undefined;

          if (status === MfaErrorStatus.RESEND_LIMIT_REACHED) {
            setResendLimitReached(true);
            return;
          }

          if (status === MfaErrorStatus.MISSING_CODE_OR_TOKEN) {
            setSessionExpired(true);
            setError('code', {
              type: 'manual',
              message: t('auth.mfa.session_expired') as string,
            });

            return;
          }

          const message = status
            ? (t(`auth.mfa.errors.${status}`, {
                remainingSeconds: data?.remainingSeconds,
              }) as string)
            : (t('auth.mfa.errors.UNKNOWN') as string);

          setResendError(message);
        },
      },
    );
  }, [tempToken, resendCodeByEmail, t, setError]);

  const handleBackToLogin = useCallback(() => {
    history.replace(ROUTES.LOGIN);
    dispatch(clearMfaContext());
  }, [dispatch, history]);

  // The MFA context is cleared on success: stay mounted for the overlay until the reload.
  if (!tempToken && !isSuccess) {
    return null;
  }

  return (
    <S.Container>
      {blockedAccount && (
        <>
          <S.BlockedAccountWarning>
            {t('auth.mfa.tooManyAttempts')}
          </S.BlockedAccountWarning>
          <S.Timer>{t('auth.mfa.tooManyAttemptsDetails')}</S.Timer>
          <S.Timer>
            <Trans
              i18nKey="auth.mfa.fromLoginPage"
              components={{
                loginLink: (
                  <LinkWithUIGuidelines
                    url="/auth"
                    isInternalLink
                    textLink=""
                    color={theme.colors.paletteFromBO.main}
                    withUnderline
                  />
                ),
              }}
            />
          </S.Timer>
          <S.Infos>
            <Trans
              i18nKey="auth.mfa.ifPersistentIssues"
              values={{ email: programEmail }}
              components={emailLinkComponents}
            />
          </S.Infos>
        </>
      )}
      {!blockedAccount && (
        <>
          <S.Title>{t('auth.mfa.title')}</S.Title>
          <S.Subtitle>{t('auth.mfa.subtitle')}</S.Subtitle>

          <S.Form onSubmit={handleSubmit(onSubmit)}>
            <S.CodeInputContainer>
              <TextField
                name="code"
                inputRef={register({
                  required: t('auth.mfa.code_required') as string,
                  pattern: {
                    value: /^\d{6}$/,
                    message: t('auth.mfa.code_invalid_format') as string,
                  },
                })}
                label={t('auth.mfa.code_label')}
                placeholder={t('auth.mfa.code_placeholder')}
                variant="outlined"
                fullWidth
                autoFocus
                type="tel"
                error={!!errors.code}
                helperText={errors.code?.message}
                onKeyDown={(e) => {
                  if (
                    e.ctrlKey ||
                    e.metaKey ||
                    [
                      'Backspace',
                      'Delete',
                      'ArrowLeft',
                      'ArrowRight',
                      'ArrowUp',
                      'ArrowDown',
                      'Tab',
                      'Home',
                      'End',
                      'Enter',
                    ].includes(e.key)
                  ) {
                    return;
                  }
                  if (!/^[0-9]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const input = e.currentTarget as HTMLInputElement;
                  const digitsOnly = String(input.value)
                    .replace(/\D+/g, '')
                    .slice(0, 6);
                  if (input.value !== digitsOnly) {
                    input.value = digitsOnly;
                  }
                }}
                onPaste={(e) => {
                  const pasted = e.clipboardData?.getData('text') ?? '';
                  const digitsOnly = String(pasted)
                    .replace(/\D+/g, '')
                    .slice(0, 6);
                  if (digitsOnly !== pasted) {
                    e.preventDefault();
                    const el = e.currentTarget as HTMLInputElement;
                    const start = el.selectionStart ?? el.value.length;
                    const end = el.selectionEnd ?? el.value.length;
                    const next = (
                      el.value.slice(0, start) +
                      digitsOnly +
                      el.value.slice(end)
                    ).slice(0, 6);
                    el.value = next;
                    el.dispatchEvent(new Event('input', { bubbles: true }));
                  }
                }}
                inputProps={{
                  maxLength: 6,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  autoComplete: 'one-time-code',
                }}
              />
            </S.CodeInputContainer>

            <S.SubmitButtonContainer>
              <PrimaryButton
                label={t('auth.mfa.verify_button')}
                isDisabled={isVerifying || isSuccess || sessionExpired}
                isLoading={isVerifying}
              />
            </S.SubmitButtonContainer>
          </S.Form>

          {!sessionExpired && (
            <>
              <S.ResendContainer>
                {!resendLimitReached && (
                  <CountdownTimer endTime={resendEndTime}>
                    {(seconds) => {
                      const padded = String(Math.max(0, seconds)).padStart(
                        2,
                        '0',
                      );
                      return (
                        <>
                          <S.Timer>
                            {t('auth.mfa.resend_timer_formatted', {
                              timer: `00:${padded}`,
                            })}
                          </S.Timer>

                          <S.ResendButton
                            onClick={handleResendCodeByEmail}
                            disabled={
                              seconds > 0 ||
                              isResendingByEmail ||
                              resendLimitReached
                            }
                            type="button"
                          >
                            {isResendingByEmail
                              ? t('auth.mfa.resending')
                              : t('auth.mfa.resend_code')}
                          </S.ResendButton>
                        </>
                      );
                    }}
                  </CountdownTimer>
                )}
                {resendLimitReached && (
                  <S.ResendLimitWarning>
                    <Trans
                      i18nKey="auth.mfa.resend_limit_exceeded"
                      values={{ email: programEmail }}
                      components={emailLinkComponents}
                    />
                  </S.ResendLimitWarning>
                )}
              </S.ResendContainer>
              <br />
              {!resendLimitReached && resendSuccess && (
                <S.ResendSuccess>
                  <Trans
                    i18nKey="auth.mfa.resend_success"
                    values={{ email: programEmail }}
                    components={emailLinkComponents}
                  />
                </S.ResendSuccess>
              )}
              {!resendLimitReached && resendError && !resendSuccess && (
                <S.ResendError>{resendError}</S.ResendError>
              )}
            </>
          )}

          <S.BackToLoginContainer>
            <LinkWithUIGuidelines
              url={ROUTES.LOGIN}
              textLink={t('auth.mfa.back_to_login')}
              isInternalLink
              color={theme.colors.paletteFromBO.main}
              onClick={handleBackToLogin}
            />
          </S.BackToLoginContainer>
        </>
      )}
      {isSuccess && (
        <SubmittingOverlay title={t('auth.mfa.verified_logging_in')} />
      )}
    </S.Container>
  );
}
